import type { History } from 'history'
import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { useAppContext } from '@/app-context'
import PrivateHeader from '@/components/layout/header'
import PrivateSidebar from '@/components/layout/sidebar'
import { privatePages } from '@/config/menu'

const getDashboard = (pathname: string): any => {
  const allPages = Object.values(privatePages) as Array<{
    path: string
    dashboardType?: string
  }>

  // First try exact match
  const exactMatch = allPages.find((page) => page.path === pathname)
  if (exactMatch) {
    return exactMatch
  }

  // Then try pattern match for routes with params
  const patternMatch = allPages.find((page) => {
    const pattern = page.path.replace(/:[^/]+/g, '[^/]+')
    const regex = new RegExp(`^${pattern}$`)
    return regex.test(pathname)
  })

  if (patternMatch) {
    return patternMatch
  }

  return null
}

type PrivateLayoutProps = {
  children: React.ReactNode
  history: History
}

const PrivateLayout: React.FC<PrivateLayoutProps> = ({ children, history }) => {
  const location = useLocation()
  const { isSidebarOpen, setIsSidebarOpen, profile } = useAppContext()

  const dashboard = useMemo(
    () => getDashboard(location.pathname),
    [location.pathname]
  )

  const showSidebar = dashboard?.dashboardType === '1'
  const showHeader = dashboard?.dashboardType !== '3'

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false)
  }

  return (
    <div className="relative min-h-screen">
      {showSidebar && (
        <PrivateSidebar
          history={history}
          location={location}
          isOpen={isSidebarOpen}
          onClose={handleCloseSidebar}
        />
      )}

      {isSidebarOpen && showSidebar && (
        <div
          className="absolute inset-0 z-[998] bg-black/50 lg:hidden"
          onClick={handleCloseSidebar}
          role="presentation"
          aria-hidden="true"
        />
      )}

      <div
        className={`bg-neutral-gray-25 min-h-screen ${showSidebar ? 'lg:ml-[256px]' : ''}`}
      >
        <div className="h-screen overflow-x-auto">
          {showHeader && (
            <PrivateHeader
              type={dashboard?.dashboardType || '1'}
              title={{
                label: dashboard?.title?.label || '',
                withBack: dashboard?.title?.withBack || false,
                backPath: dashboard?.title?.backPath
              }}
              history={history}
              location={location}
              onToggleSidebar={
                showSidebar ? () => setIsSidebarOpen(!isSidebarOpen) : undefined
              }
              isLoggedIn={!!profile}
            />
          )}
          {children}
        </div>
      </div>
    </div>
  )
}

export default PrivateLayout
